import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { Icono, type NombreIcono } from './Icono';

export type TonoAviso = 'exito' | 'error' | 'aviso' | 'info';

export interface Aviso {
  tono?: TonoAviso;
  titulo: string;
  cuerpo?: string;
  icono?: NombreIcono;
  /** En milisegundos. */
  duracion?: number;
}

interface ContextoAvisos {
  mostrarAviso: (aviso: Aviso) => void;
  cerrarAviso: () => void;
}

const ICONO_POR_TONO: Record<TonoAviso, NombreIcono> = {
  exito: 'check-circle',
  error: 'alert-circle',
  aviso: 'alert-triangle',
  info: 'info',
};

const Contexto = createContext<ContextoAvisos | null>(null);

// Se muestra un aviso por vez: si llega otro, reemplaza al anterior. Dos carteles
// apilados sobre la misma acción confunden más de lo que informan.
export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const temporizador = useRef<number | undefined>(undefined);

  const cerrarAviso = useCallback(() => {
    window.clearTimeout(temporizador.current);
    setAviso(null);
  }, []);

  const mostrarAviso = useCallback((nuevo: Aviso) => {
    window.clearTimeout(temporizador.current);
    setAviso(nuevo);
    temporizador.current = window.setTimeout(() => { setAviso(null); }, nuevo.duracion ?? 4000);
  }, []);

  useEffect(() => () => { window.clearTimeout(temporizador.current); }, []);

  const valor = useMemo(() => ({ mostrarAviso, cerrarAviso }), [mostrarAviso, cerrarAviso]);
  const tono = aviso?.tono ?? 'info';

  return (
    <Contexto.Provider value={valor}>
      {children}
      {/* La región viva existe siempre: si apareciera junto con el texto, varios
          lectores de pantalla no la registran a tiempo y el aviso no se lee. */}
      <div className="avisos" role="status" aria-live="polite">
        {aviso && (
          <div className={`aviso aviso--${tono}`}>
            <Icono nombre={aviso.icono ?? ICONO_POR_TONO[tono]} tamano={18} className="aviso__icono" />
            <div className="aviso__textos">
              <div className="aviso__titulo">{aviso.titulo}</div>
              {aviso.cuerpo && <div className="aviso__cuerpo">{aviso.cuerpo}</div>}
            </div>
            <button type="button" className="aviso__cerrar" onClick={cerrarAviso} aria-label="Cerrar aviso">
              <Icono nombre="x" tamano={15} />
            </button>
          </div>
        )}
      </div>
    </Contexto.Provider>
  );
}

export function useAvisos(): ContextoAvisos {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAvisos se usa adentro de ProveedorAvisos');
  return contexto;
}
